import { DEVICE_TABLE_TYPE } from '@/shared/config';
import { gettextAllDevicesSubtab } from '@/shared/lib';
import { useEffect, useRef, useState } from 'react';
import type { DevicesTableWithSubTabProps } from './devicesTableWithSubTab.types';

/**
 * Supplies a devices table with its sub-tab filter options and the active filter. Holds the
 * all-devices and unused-devices option lists in refs, starts from the given sub-tab or the tab
 * type's default, and reports the filter to the navigation callback when the table becomes active
 * or the filter changes.
 *
 * @param isActive - Whether the table's tab is active.
 * @param type - Device table type, which selects the unused-devices defaults.
 * @param updateSubTabGoto - Records the chosen sub-tab for the table type.
 * @param subTab - Sub-tab to start on, when restored from navigation.
 * @returns An object carrying both filter option lists, the active filter and its click handler.
 */
const useDevicesTableWithSubTab = ({
  isActive,
  type,
  updateSubTabGoto,
  subTab,
}: DevicesTableWithSubTabProps) => {
  const filterAllDeviceList = useRef([
    {
      label: gettextAllDevicesSubtab(DEVICE_TABLE_TYPE.IN_STUDY),
      value: DEVICE_TABLE_TYPE.IN_STUDY,
      id: 'in-study-devices',
    },
    {
      label: gettextAllDevicesSubtab(DEVICE_TABLE_TYPE.AWAITING_UPLOAD),
      value: DEVICE_TABLE_TYPE.AWAITING_UPLOAD,
      id: 'awaiting-upload-devices',
    },
    {
      label: gettextAllDevicesSubtab(DEVICE_TABLE_TYPE.NOT_IN_STUDY),
      value: DEVICE_TABLE_TYPE.NOT_IN_STUDY,
      id: 'not-in-study-devices',
    },
    {
      label: gettextAllDevicesSubtab(DEVICE_TABLE_TYPE.RMA),
      value: DEVICE_TABLE_TYPE.RMA,
      id: 'rma-devices',
    },
    {
      label: gettextAllDevicesSubtab(DEVICE_TABLE_TYPE.ALL),
      value: DEVICE_TABLE_TYPE.ALL,
      id: 'all-devices',
    },
  ]);

  const filterUnusedDeviceList = useRef([
    {
      label: 'All time',
      value: DEVICE_TABLE_TYPE.UNUSED_ALL,
      id: 'unused-all',
    },
    {
      label: '1 month',
      value: DEVICE_TABLE_TYPE.UNUSED_ONEMONTH,
      id: 'unused-onemonth',
    },
    {
      label: '2 months',
      value: DEVICE_TABLE_TYPE.UNUSED_TWOMONTH,
      id: 'unused-twomonth',
    },
    {
      label: '3 months',
      value: DEVICE_TABLE_TYPE.UNUSED_THREEMONTH,
      id: 'unused-threemonth',
    },
    {
      label: '6 months',
      value: DEVICE_TABLE_TYPE.UNUSED_SIXMONTH,
      id: 'unused-sixmonth',
    },
    {
      label: '1 year',
      value: DEVICE_TABLE_TYPE.UNUSED_ONEYEAR,
      id: 'unused-oneyear',
    },
  ]);

  const isUnused = type === DEVICE_TABLE_TYPE.UNUSED_DEVICES_TAB;

  const [filterDevice, setFilterDevice] = useState(
    subTab || (isUnused ? DEVICE_TABLE_TYPE.UNUSED_ALL : DEVICE_TABLE_TYPE.IN_STUDY),
  );

  /**
   * Applies a device sub-tab filter selection on the devices table. Skips when value already equals filterDevice, otherwise records it on the shared device navigation state under the table's type and stores it as the active filter.
   *
   * @param value - Filter value chosen in the sub-tab.
   * @returns Nothing.
   */
  const onClickFilterDevice = (value: any) => {
    if (value !== filterDevice) {
      updateSubTabGoto?.(type, value);
      setFilterDevice(value);
    }
  };

  useEffect(() => {
    if (isActive) {
      updateSubTabGoto?.(type, filterDevice);
    }
  }, [isActive]);

  return { filterAllDeviceList, filterUnusedDeviceList, filterDevice, onClickFilterDevice };
};

export { useDevicesTableWithSubTab };
